/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { Icon, TextField, Button, DatePicker, localToday } from 'sarsaparilla';
import { parseDate } from '@internationalized/date';

const propTypes = {
    index: PropTypes.number.isRequired,
    removeMember: PropTypes.func,
    handleMemberInfoChange: PropTypes.func,
    member: PropTypes.shape({
        first_name: PropTypes.string,
        last_name: PropTypes.string,
        middle_name: PropTypes.string,
        birth_date: PropTypes.string,
    }),
    reservation: PropTypes.shape({
        group_members: PropTypes.arrayOf(
            PropTypes.shape({
                first_name: PropTypes.string,
                last_name: PropTypes.string,
                middle_name: PropTypes.string,
                birth_date: PropTypes.string,
            })
        ),
    }),
    validation: PropTypes.shape({}),
    disableRemove: PropTypes.bool,
    hasBirthdate: PropTypes.bool,
    addToInputRefs: PropTypes.func.isRequired,
    vIndex: PropTypes.number.isRequired,
};

const defaultProps = {
    validation: {},
    hasBirthdate: true,
};

export default class GroupMemberItem extends React.Component {
    static propTypes = propTypes;

    static defaultProps = defaultProps;

    constructor(props) {
        super(props);
        this.onClickRemove = this.onClickRemove.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.handleBirthDateChange = this.handleBirthDateChange.bind(this);
        this.datePickerValidator = this.datePickerValidator.bind(this);
    }

    state = {
        datePickerValue: null,
    };

    componentDidMount() {
        const { reservation, index } = this.props;
        const birthDate = reservation.group_members?.[index]?.birth_date;

        this.setState({
            datePickerValue: birthDate ? parseDate(birthDate) : null,
        });
    }

    handleChange(event) {
        const input = event.target.value;
        const id = event.target.id.replace(this.props.index, '');
        this.props.handleMemberInfoChange(this.props.index, id, input);
    }

    handleBirthDateChange(value, validation) {
        let dateStr = '';

        if (value && !validation.isInvalid) {
            dateStr = value.toString();
        }

        this.props.handleMemberInfoChange(this.props.index, 'birth_date', dateStr);

        this.props.handleMemberInfoChange(
            this.props.index,
            'attributes.birth_date',
            dateStr
        );

        this.setState({
            datePickerValue: value,
        });
    }

    onClickRemove() {
        const addkey = this.props.vIndex + (this.props.index - 1);
        const refIds = [addkey + 30, addkey + 33, addkey + 34];
        this.props.removeMember(this.props.index, refIds);
    }

    isDateTooFarInThePast(dateToCompare) {
        const MAX_PAST_DATE = localToday.subtract({ years: 120 });

        return dateToCompare.compare(MAX_PAST_DATE) < 0;
    }

    isDateInTheFuture(dateToCompare) {
        return dateToCompare.compare(localToday) > 0;
    }

    datePickerValidator(value, label) {
        const isPastDate = this.isDateTooFarInThePast(value);
        const isFutureDate = this.isDateInTheFuture(value);
        let errorMessage = '';

        if (isPastDate) {
            errorMessage = `${label} is too far in the past.`;
        }

        if (isFutureDate) {
            errorMessage = `${label} can not be in the future.`;
        }

        return {
            isInvalid: isPastDate || isFutureDate,
            errorMessage,
        };
    }

    isDisabled() {
        return this.props.index === 0;
    }

    renderRemoveMemberButton() {
        if (this.isDisabled() || this.props.disableRemove) return null;

        return (
            <div className="per-group-member-item-cancel">
                <Button
                    type="button"
                    appearance="subtle-danger"
                    onClick={this.onClickRemove}
                    iconBeforeElement={<Icon iconName="delete" />}
                    aria-label="Remove"
                />
            </div>
        );
    }

    renderBirthdate() {
        const { index, addToInputRefs, vIndex, hasBirthdate } = this.props;

        return (
            hasBirthdate && (
                <div className="rec-nested-wrap">
                    <div className="rec-grid-6-12">
                        <div className="rec-form-item-wrap">
                            <div className="rec-grid-12-12">
                                <DatePicker
                                    isRequired
                                    autoOpenCalendar={false}
                                    style={{ display: 'inline-block' }}
                                    ref={addToInputRefs(vIndex + (index - 1) + 30)}
                                    id={`member_${index}_birth_date`}
                                    label="Birth Date"
                                    value={this.state.datePickerValue}
                                    onChange={this.handleBirthDateChange}
                                    validator={this.datePickerValidator}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )
        );
    }

    render() {
        const { index, member, addToInputRefs, vIndex } = this.props;
        let formComponent = null;
        if (!this.isDisabled()) {
            formComponent = (
                <div className="rec-nested-wrap">
                    <div className="rec-grid-4-12">
                        <div className="rec-form-item-wrap">
                            <TextField
                                id={`${index}first_name`}
                                label="First Name"
                                ref={addToInputRefs(vIndex + (index - 1) * 2 + 34)}
                                readOnly={this.isDisabled()}
                                disabled={this.isDisabled()}
                                value={member.first_name}
                                isRequired
                                onChange={this.handleChange}
                            />
                        </div>
                    </div>
                    <div className="rec-grid-2-12">
                        <div className="rec-form-item-wrap">
                            <TextField
                                id={`${index}middle_name`}
                                label="MI"
                                readOnly={this.isDisabled()}
                                disabled={this.isDisabled()}
                                value={member.middle_name}
                                onChange={this.handleChange}
                            />
                        </div>
                    </div>
                    <div className="rec-grid-6-12">
                        <div className="rec-form-item-wrap">
                            <TextField
                                id={`${index}last_name`}
                                label="Last Name"
                                ref={addToInputRefs(vIndex + (index - 1) * 2 + 33)}
                                readOnly={this.isDisabled()}
                                disabled={this.isDisabled()}
                                value={member.last_name}
                                isRequired
                                onChange={this.handleChange}
                            />
                        </div>
                    </div>
                </div>
            );
        }
        return (
            <div className="per-group-member-item">
                <div className="per-group-member-item-header">
                    <div className="per-group-member-item-title">
                        Member {this.props.index + 1}:{' '}
                        <b>{`${member.first_name || ''} ${member.middle_name || ''} ${member.last_name || ''}`}</b>
                    </div>
                    {this.renderRemoveMemberButton()}
                </div>
                <div className="per-group-member-item-body">
                    {formComponent}
                    {this.renderBirthdate()}
                </div>
            </div>
        );
    }
}

// cspell:ignore Birthdate, addkey
